// =============================================================================
// Admin Subscriptions Page — cross-tenant subscription view + force cancel
// -----------------------------------------------------------------------------
// Lists every subscription with status / provider filters and lets the
// operator drill into a single org to view detail and force-cancel. Force
// cancels emit an audit-log row server-side; we collect a reason text
// here for the admin audit trail.
// =============================================================================

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { useApiClient } from '@tkpod/api-client';
import type {
  CancelSubscriptionResponse,
  SubscriptionDto,
} from '@tkpod/shared';
import { Button, Card, CardHeader, Modal, ModalFooter, Spinner } from '@tkpod/ui';

interface AdminSubscriptionsListResponse {
  items: Array<{
    subscription: SubscriptionDto;
    organizationName: string;
  }>;
  page: number;
  limit: number;
  total: number;
}

const STATUS_OPTIONS = [
  '',
  'ACTIVE',
  'TRIALING',
  'PAST_DUE',
  'CANCELLED',
  'PAUSED',
  'INCOMPLETE',
  'INCOMPLETE_EXPIRED',
  'UNPAID',
] as const;

const PROVIDER_OPTIONS = ['', 'SHOPIFY', 'STRIPE'] as const;

function formatCents(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

export function AdminSubscriptionsPage() {
  const [params, setParams] = useSearchParams();
  const selectedOrg = params.get('org');

  if (selectedOrg) {
    return (
      <AdminSubscriptionDetail
        organizationId={selectedOrg}
        onBack={() => {
          const next = new URLSearchParams(params);
          next.delete('org');
          setParams(next, { replace: true });
        }}
      />
    );
  }

  return <AdminSubscriptionsList />;
}

function AdminSubscriptionsList() {
  const api = useApiClient();
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);
  const status = params.get('status') ?? '';
  const provider = params.get('provider') ?? '';
  const search = params.get('search') ?? '';

  const query = useQuery<AdminSubscriptionsListResponse>({
    queryKey: ['admin', 'subscriptions', { page, status, provider, search }],
    queryFn: () => {
      const qs = new URLSearchParams();
      qs.set('page', String(page));
      qs.set('limit', '25');
      if (status) qs.set('status', status);
      if (provider) qs.set('provider', provider);
      if (search) qs.set('search', search);
      return api.get<AdminSubscriptionsListResponse>(
        `/api/v1/admin/subscriptions?${qs}`,
      );
    },
  });

  function patch(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: true });
  }

  return (
    <Card padding="none">
      <div className="flex flex-col gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-lg font-semibold text-text-primary">
            Subscriptions
          </h1>
          <p className="text-xs text-text-tertiary">
            Cross-tenant view. Filter by status / provider to triage and
            click an organization to inspect detail or force-cancel.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            placeholder="Search organization…"
            defaultValue={search}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                patch('search', (e.target as HTMLInputElement).value);
              }
            }}
            className="w-56 rounded-md border border-border bg-surface px-3 py-1.5 text-sm focus:border-brand-600 focus:outline-none"
          />
          <select
            value={status}
            onChange={(e) => patch('status', e.target.value)}
            className="rounded-md border border-border bg-surface px-2 py-1.5 text-sm"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s || 'All statuses'}
              </option>
            ))}
          </select>
          <select
            value={provider}
            onChange={(e) => patch('provider', e.target.value)}
            className="rounded-md border border-border bg-surface px-2 py-1.5 text-sm"
          >
            {PROVIDER_OPTIONS.map((p) => (
              <option key={p} value={p}>
                {p || 'All providers'}
              </option>
            ))}
          </select>
        </div>
      </div>

      {query.isLoading ? (
        <div className="flex h-32 items-center justify-center">
          <Spinner />
        </div>
      ) : query.isError ? (
        <div className="space-y-2 px-5 py-6">
          <p className="text-sm text-rose-600">
            {query.error instanceof Error
              ? query.error.message
              : 'Unable to load subscriptions.'}
          </p>
          <Button variant="secondary" onClick={() => query.refetch()}>
            Retry
          </Button>
        </div>
      ) : !query.data?.items.length ? (
        <p className="px-5 py-12 text-center text-sm text-text-secondary">
          No subscriptions match your filters.
        </p>
      ) : (
        <>
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border bg-surface-tertiary text-xs uppercase tracking-wide text-text-tertiary">
              <tr>
                <th className="px-5 py-2 font-semibold">Organization</th>
                <th className="px-5 py-2 font-semibold">Plan</th>
                <th className="px-5 py-2 font-semibold">Provider</th>
                <th className="px-5 py-2 font-semibold">Status</th>
                <th className="px-5 py-2 text-right font-semibold">Price</th>
                <th className="px-5 py-2 font-semibold">Period end</th>
              </tr>
            </thead>
            <tbody>
              {query.data.items.map((row) => (
                <tr
                  key={row.subscription.id}
                  className="cursor-pointer border-b border-border/60 hover:bg-surface-tertiary/60"
                  onClick={() => patch('org', row.subscription.organizationId)}
                >
                  <td className="px-5 py-3 align-top">
                    <p className="font-medium text-text-primary">
                      {row.organizationName}
                    </p>
                    <p className="text-xs text-text-tertiary">
                      {row.subscription.organizationId}
                    </p>
                  </td>
                  <td className="px-5 py-3 align-top text-text-primary">
                    {row.subscription.plan.name}
                  </td>
                  <td className="px-5 py-3 align-top text-text-secondary">
                    {row.subscription.provider}
                  </td>
                  <td className="px-5 py-3 align-top">
                    <StatusBadge status={row.subscription.status} />
                    {row.subscription.cancelAtPeriodEnd && (
                      <span className="ml-2 text-[10px] uppercase tracking-wide text-amber-700">
                        Cancels at period end
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right align-top text-text-primary">
                    {formatCents(row.subscription.plan.monthlyPriceUsdCents)}
                    <span className="ml-1 text-xs text-text-tertiary">/mo</span>
                  </td>
                  <td className="px-5 py-3 align-top text-xs text-text-tertiary">
                    {new Date(
                      row.subscription.currentPeriodEnd,
                    ).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex items-center justify-between border-t border-border px-5 py-3 text-xs text-text-secondary">
            <span>
              Page {page} • {query.data.total} subscription
              {query.data.total === 1 ? '' : 's'} total
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                disabled={page <= 1}
                onClick={() => patch('page', String(page - 1))}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={page * query.data.limit >= query.data.total}
                onClick={() => patch('page', String(page + 1))}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </Card>
  );
}

function AdminSubscriptionDetail({
  organizationId,
  onBack,
}: {
  organizationId: string;
  onBack: () => void;
}) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const [cancelOpen, setCancelOpen] = useState(false);

  const query = useQuery<{ subscription: SubscriptionDto | null }>({
    queryKey: ['admin', 'subscription', organizationId],
    queryFn: () =>
      api.get<{ subscription: SubscriptionDto | null }>(
        `/api/v1/admin/subscriptions/${organizationId}`,
      ),
  });

  const cancelMutation = useMutation({
    mutationFn: (body: { immediate: boolean; reason: string }) =>
      api.post<CancelSubscriptionResponse>(
        `/api/v1/admin/subscriptions/${organizationId}/cancel`,
        body,
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(['admin', 'subscription', organizationId], {
        subscription: data.subscription,
      });
      setCancelOpen(false);
    },
  });

  const sub = query.data?.subscription ?? null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack}>
          ← Back to subscriptions
        </Button>
        <Link
          to={`/tenants/${organizationId}`}
          className="text-sm font-medium text-brand-600 hover:underline"
        >
          View organization →
        </Link>
      </div>

      <Card>
        <CardHeader title="Subscription detail" />
        <div className="space-y-4 px-4 pb-5 sm:px-5">
          {query.isLoading ? (
            <Spinner />
          ) : query.isError ? (
            <p className="text-sm text-rose-600">
              {query.error instanceof Error
                ? query.error.message
                : 'Unable to load subscription.'}
            </p>
          ) : !sub ? (
            <p className="text-sm text-text-secondary">
              Organization is on the implicit Free plan — no subscription on
              record.
            </p>
          ) : (
            <>
              <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                <Field label="Plan" value={sub.plan.name} />
                <Field label="Status" value={sub.status} />
                <Field label="Provider" value={sub.provider} />
                <Field
                  label="Monthly price"
                  value={formatCents(sub.plan.monthlyPriceUsdCents)}
                />
                <Field
                  label="Period start"
                  value={new Date(sub.currentPeriodStart).toLocaleString()}
                />
                <Field
                  label="Period end"
                  value={new Date(sub.currentPeriodEnd).toLocaleString()}
                />
                <Field
                  label="Cancel at period end"
                  value={sub.cancelAtPeriodEnd ? 'Yes' : 'No'}
                />
                {sub.canceledAt && (
                  <Field
                    label="Cancelled at"
                    value={new Date(sub.canceledAt).toLocaleString()}
                  />
                )}
                {sub.shopifyChargeId && (
                  <Field
                    label="Shopify charge ID"
                    value={sub.shopifyChargeId}
                  />
                )}
                {sub.stripeSubscriptionId && (
                  <Field
                    label="Stripe subscription ID"
                    value={sub.stripeSubscriptionId}
                  />
                )}
              </dl>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="danger"
                  onClick={() => setCancelOpen(true)}
                  disabled={
                    sub.status === 'CANCELLED' ||
                    sub.status === 'INCOMPLETE_EXPIRED'
                  }
                >
                  Force cancel
                </Button>
              </div>
            </>
          )}
        </div>
      </Card>

      <ForceCancelModal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onSubmit={(payload) => cancelMutation.mutate(payload)}
        loading={cancelMutation.isPending}
        error={
          cancelMutation.error instanceof Error
            ? cancelMutation.error.message
            : null
        }
      />
    </div>
  );
}

function StatusBadge({ status }: { status: SubscriptionDto['status'] }) {
  const map: Record<string, string> = {
    ACTIVE: 'bg-emerald-50 text-emerald-700',
    TRIALING: 'bg-sky-50 text-sky-700',
    PAST_DUE: 'bg-amber-50 text-amber-700',
    CANCELLED: 'bg-rose-50 text-rose-700',
    PAUSED: 'bg-amber-50 text-amber-700',
    INCOMPLETE: 'bg-amber-50 text-amber-700',
    INCOMPLETE_EXPIRED: 'bg-rose-50 text-rose-700',
    UNPAID: 'bg-rose-50 text-rose-700',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
        map[status] ?? 'bg-surface-tertiary text-text-secondary'
      }`}
    >
      {status}
    </span>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-text-tertiary">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm text-text-primary">{value}</dd>
    </div>
  );
}

function ForceCancelModal({
  open,
  onClose,
  onSubmit,
  loading,
  error,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: { immediate: boolean; reason: string }) => void;
  loading: boolean;
  error: string | null;
}) {
  const [immediate, setImmediate] = useState(true);
  const [reason, setReason] = useState('');
  const [validation, setValidation] = useState<string | null>(null);

  const ready = useMemo(() => reason.trim().length >= 3, [reason]);

  function handleSubmit() {
    setValidation(null);
    if (!ready) {
      setValidation('Reason must be at least 3 characters.');
      return;
    }
    onSubmit({ immediate, reason: reason.trim() });
  }

  return (
    <Modal
      open={open}
      onClose={loading ? () => {} : onClose}
      title="Force cancel subscription?"
      description="This bypasses the merchant flow and is recorded in the audit log. For Shopify-billed orgs the cancellation is forwarded to the Shopify Admin API."
      size="md"
    >
      <div className="space-y-4">
        <label className="flex items-start gap-3 text-sm">
          <input
            type="checkbox"
            checked={immediate}
            onChange={(e) => setImmediate(e.target.checked)}
            disabled={loading}
            className="mt-1 h-4 w-4 rounded border-border text-brand-600 focus:ring-brand-600"
          />
          <span>
            Cancel immediately (vs. at period end)
            <span className="block text-xs text-text-secondary">
              Immediate cancellation severs access right away. Period-end
              cancellations let the merchant keep paid features until renewal.
            </span>
          </span>
        </label>
        <div>
          <label
            htmlFor="cancel-reason"
            className="mb-1 block text-xs font-medium uppercase tracking-wide text-text-tertiary"
          >
            Reason (audit log)
          </label>
          <textarea
            id="cancel-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            disabled={loading}
            placeholder="Merchant request via support ticket #..."
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:border-brand-600 focus:outline-none"
          />
        </div>
        {(validation || error) && (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900">
            {validation ?? error}
          </div>
        )}
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="danger" onClick={handleSubmit} disabled={loading}>
          {loading ? 'Cancelling…' : 'Force cancel'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
